/**
 * This is the Viewer-side part of the "ClientAPI". A customer will embed an iFrame containing the Viewer
 * into their page which also sources the ClientApi.js script. That script communicates with viewer via
 * messages, which are received here.
 */
import * as ViewerApi from './ViewerInterface';
import * as tracking from '../helpers/tracking';

// @TODO: Need a good way to generate this. We should probably automate it during release deployment
// Let the Client know which version it's talking to. Client's should include this in any bug reports they file
const kViewerVersion = '1';

// The functions that will be exposed to the Client via the IFrameApi. These should be based on the clientVersion.
const ClientApiFunctions = {
  addSegment: ViewerApi.addSegment,
  addActivity: ViewerApi.addActivity,
  toggleActivityVisibility: ViewerApi.toggleActivityVisibility,
  deleteActivity: ViewerApi.deleteActivity,
  setTargetActivity: ViewerApi.setTargetActivity,
  addPoints: ViewerApi.addPoints,
  setTime: ViewerApi.setTime,
  getTime: ViewerApi.getTime,
  addMedia: ViewerApi.addMedia,
  toggleMediaVisibility: ViewerApi.toggleMediaVisibility,
  deleteMedia: ViewerApi.deleteMedia,
};

class IFrameApi {
  constructor() {
    this.clientVersion = undefined;
    this.clientApiKey = undefined;
    this.clientWindow = undefined;
    this.clientOrigin = undefined;
    this.clientFunctions = undefined;
    this.clientLocation = undefined;
  }

  initialize() {
    // All ClientAPI functions are callable once App.onSceneLoad has executed
    ViewerApi.onceSceneLoaded().then(() => {
      // Listen only for an 'init' message. Once the handshake is successful, hook up the message listener
      const initMessageListener = event => {
        const success = this._tryInitialize(event);
        if (success) {
          window.removeEventListener('message', initMessageListener, false);
        }
      };

      window.addEventListener('message', initMessageListener, false);
      window.parent.postMessage({ func: 'handshake' }, '*');
    });
  }

  _tryInitialize(event) {
    if (event.data.func === 'handshake') {
      this.clientVersion = event.data.clientApiVersion;
      this.clientApiKey = event.data.apiKey;
      this.clientWindow = event.source;
      this.clientOrigin = event.origin;
      this.clientLocation = event.origin;
      this.clientFunctions = Object.keys(ClientApiFunctions);
      window.addEventListener('message', this._receiveMessage.bind(this), false);
      this._sendMessage({ func: 'interface', viewerVersion: kViewerVersion, functions: this.clientFunctions });
      return true;
    }

    // This must not have been a message meant for us
    return false;
  }

  _sendMessage(data) {
    return this.clientWindow.postMessage(data, this.clientOrigin);
  }

  _receiveMessage(event) {
    // Only accept messages with the origin which performed the handshake
    if (event.origin === this.clientOrigin) {
      const messageId = event.data.messageId;
      const options = event.data.options;
      const funcName = event.data.func;
      const func = ClientApiFunctions[funcName];
      tracking.addClientApiEvent(funcName, this.clientVersion, this.clientApiKey, this.clientLocation);

      if (func) {
        func(options)
          .then(result => this._sendMessage({ messageId, result }))
          .catch(error => this._sendMessage({ messageId, error: error.message }));
      }
    }
  }
}

export default IFrameApi;
